import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Zap, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { loginUser, verifyTwoFactor, resendTwoFactor, clearTwoFactor } from "../../store/slices/authSlice";
import toast from "react-hot-toast";
import { getPublicProperties } from "../../api/publicApi";
import collingsLogo from "../../assets/collingslogo.png";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { status, user, twoFactor } = useSelector((state) => state.auth);

  const [loginType, setLoginType] = useState("agent");
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [validationError, setValidationError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  // "Don't ask again on this device for 30 days" — ticked by default for convenience.
  const [rememberDevice, setRememberDevice] = useState(true);

  // Redirect already logged-in users to dashboard
  useEffect(() => {
    // If user is already logged in when they land on /login, redirect them
    if (user) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on component mount

  useEffect(() => {
    if (user) {
      console.log("Login successful, user detected in state:", user);
      toast.success("Logged in successfully!");
      // Adding a tiny delay to ensure state and cookies are ready before navigation
      setTimeout(() => {
        console.log("Navigating to dashboard...");
        navigate("/");
      }, 500);
    }
  }, [user, navigate]);

  useEffect(() => {
    const remembered = localStorage.getItem("rememberMe");
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    if (remembered === "true" && rememberedEmail) {
      setCredentials((prev) => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!credentials.email || !credentials.password) {
      setValidationError("Email and password are required.");
      toast.error("Email and password are required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(credentials.email)) {
      setValidationError("Please enter a valid email address.");
      toast.error("Please enter a valid email address.");
      return;
    }

    if (credentials.password.length < 6) {
      setValidationError("Password must be at least 6 characters.");
      toast.error("Password must be at least 6 characters.");
      return;
    }

    setValidationError("");

    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem("rememberedEmail", credentials.email);
    } else {
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberedEmail");
    }
    localStorage.removeItem("rememberedPassword");

    dispatch(loginUser(credentials));
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    const code = otp.trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code we texted you.");
      return;
    }
    dispatch(verifyTwoFactor({ challengeToken: twoFactor.challengeToken, otp: code, rememberDevice }));
  };

  const handleResendOtp = () => {
    dispatch(resendTwoFactor({ challengeToken: twoFactor.challengeToken }));
  };

  const handleBackToLogin = () => {
    setOtp("");
    dispatch(clearTwoFactor());
  };

  /*
  // ✅ Fetch property listings using GET API
  const handleListingView = async (e) => {
    e.preventDefault();
    toast.loading(\"Fetching property listings...\");

    try {
      const response = await getPublicProperties();
      toast.dismiss();
      // toast.success(\"Properties fetched successfully!\");

      console.log(\"Property Listing Data:\", response.data);

      // ✅ Save data in localStorage to access in PropertyViewListing
      localStorage.setItem(\"propertyListingData\", JSON.stringify(response.data));

      // ✅ Navigate to the listing view page
      navigate(\"/property-view-listing\");
    } catch (err) {
      toast.dismiss();
      console.error(\"Error fetching properties:\", err);
      toast.error(\"Failed to load property listings. Please try again.\");
    }
  };
  */

  // Two-factor step: shown after a correct password when the account has 2FA on.
  // 'totp' means the code is already being generated in an app on their phone,
  // so the screen must not tell them to wait for a text that is never coming.
  const usingAuthenticatorApp = twoFactor?.method === "totp";

  if (twoFactor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-3 sm:p-4 md:p-6 font-sans">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 w-full max-w-lg border border-gray-100">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-[#e8f8fa] flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-[#55C5D0]" />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">Verify it's you</h1>
            <p className="text-gray-600 text-sm sm:text-base mt-2">
              {usingAuthenticatorApp ? (
                <>
                  Open your authenticator app and enter the 6-digit code it shows for
                  Collings.
                </>
              ) : (
                <>
                  We sent a 6-digit code to{" "}
                  <span className="font-semibold text-gray-800">{twoFactor.maskedPhone}</span>.
                  Enter it below to finish signing in.
                </>
              )}
            </p>
          </div>

          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification code
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] text-center text-2xl tracking-[0.5em] font-semibold bg-gray-50 focus:bg-white"
                placeholder="••••••"
                autoFocus
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={() => setRememberDevice((prev) => !prev)}
                className="h-4 w-4 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] accent-[#55C5D0]"
              />
              <span className="text-sm text-gray-700">
                Don't ask again on this device for 30 days
              </span>
            </label>

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl hover:opacity-90 transition-all duration-300 font-medium text-sm sm:text-base shadow-lg disabled:opacity-50"
              style={{ backgroundColor: "#55C5D0" }}
            >
              {status === "loading" ? "Verifying..." : "Verify & Sign In"}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 font-medium"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
              {/* For an authenticator user this is not a resend, it is the way
                  out when the phone with the app on it is lost or flat. It is
                  only offered when there is a number on file to text. */}
              {usingAuthenticatorApp ? (
                twoFactor.smsFallbackAvailable ? (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    className="text-[#55C5D0] hover:underline font-medium"
                  >
                    Text me a code instead
                  </button>
                ) : null
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="text-[#55C5D0] hover:underline font-medium"
                >
                  Resend code
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-3 sm:p-4 md:p-6 font-sans">
      <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 w-full max-w-lg border border-gray-100">
        <div className="text-center mb-8 sm:mb-10">
          <div className="flex flex-col items-center justify-center mb-4 sm:mb-6">
            <div className="mb-3 sm:mb-4">
              <img
                src={collingsLogo}
                alt="Collings Real Estate"
                className="h-8 sm:h-9 w-auto max-w-[180px] object-contain mx-auto"
              />
            </div>
            <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 font-medium">
              <Zap className="w-4 h-4 text-[#55C5D0]" />
              <span className="text-center">AI-Powered Lead Generation Platform</span>
            </div>
          </div>
          <p className="text-gray-600 font-normal text-base sm:text-lg leading-relaxed">
            Australia's Most Advanced Real Estate CRM
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          {validationError && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-sm" role="alert">
              <span className="block sm:inline">{validationError}</span>
            </div>
          )}

          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-medium">
                Email Address
              </label>
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] text-base font-normal transition-all duration-200 bg-gray-50 focus:bg-white"
                placeholder={loginType === "admin" ? "admin@collings.com.au" : "sarah@collings.com.au"}
                required
              />
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2 font-medium">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:border-[#55C5D0] text-base font-normal transition-all duration-200 bg-gray-50 focus:bg-white pr-12"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#55C5D0] focus:outline-none"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center mt-2">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe((prev) => !prev)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 font-normal">
                Remember Me
              </label>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
            {/* ✅ Listing View Button triggers GET request
            <button
              type=\"button\"
              onClick={handleListingView}
              className=\"text-[#55C5D0] text-xs sm:text-sm hover:underline\"
            >
              Listing View
            </button>
            */}

            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-xs sm:text-sm text-blue-400 hover:text-blue-600 font-medium tracking-wide underline"
            >
              Forgot your password?
            </button>
          </div>

          <button
            type="submit"
            className="w-full text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl hover:opacity-90 transition-all duration-300 font-medium text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 tracking-wide"
            style={{ backgroundColor: "#55C5D0" }}
            disabled={status === "loading"}
          >
            {status === "loading" ? "Signing in..." : "Sign In to Collings"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
